import React, { useContext, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import Select from 'react-select';
import { uid } from 'uid';

import useThemeBlock from '@/hooks/useThemeBlock';
import { LocaleContext } from '@/contexts/locale-context';
import formBlock from '@/components/molecules/Form/block.json';

import block from './block.json';
import { getStyles } from './styles.css';

/**
 * TYPINGS
 */
type OptionProps = {
	label: string;
	value: string;
	disabled?: boolean;
};

type InputSelectProps = InputProps &
	ThemeProps & {
		options: string | Array<OptionProps>;
		htmlProps?: object;
		onChange?: Function;
		onBlur?: Function;
		value?: string;
	};

interface ForwardedInputSelect
	extends React.ForwardRefExoticComponent<
			InputSelectProps & React.RefAttributes<HTMLInputElement>
		>,
		BlockConfigs {}

/**
 * Build an options array from string. Format: `value:label;value:label;....`
 *
 * 1. Split by semicolon
 * 2. Trim white spaces
 * 3. Get value and label
 * 4. Create Array with values
 *
 * @param {string} options
 * @returns {Array<OptionProps>}
 */
const buildOptionsFromString = (options) =>
	options.split(';').reduce((acc, element) => {
		const trimmedElement = element.trim().replace('::', ':'); // Remove white spaces + fix duplicate separators

		if (trimmedElement) {
			const [value, label] = trimmedElement.split(':');
			acc.push({
				value: value.trim(),
				label: label.trim(),
			});
		}
		return acc;
	}, []);

/**
 * COMPONENT
 */
export const InputSelect = React.forwardRef<HTMLInputElement, InputSelectProps>(
	(
		{
			id = uid(),
			label,
			name,
			placeholder,
			fullSize = true,
			required = false,
			disabled = false,
			invalid,
			inputAttributes,
			theme = {},
			options: initOptions = '',
			onChange = () => {},
			onBlur = () => {},
			value,
			...rest
		},
		ref
	) => {
		const { blockTheme: formBlockTheme } = useThemeBlock(
			formBlock.slug,
			theme
		);
		const rootClass = cx(
			'supt-inputSelect supt-inputWrapper supt-inputField',
			fullSize ? 'supt-inputField--full' : 'supt-inputField--half',
			invalid && typeof invalid === 'string' && 'supt-inputField--error',
			getStyles(formBlockTheme)
		);

		const { __t } = useContext(LocaleContext);
		const labelId = uid();

		const options = useMemo(
			() =>
				Array.isArray(initOptions)
					? initOptions
					: buildOptionsFromString(initOptions),
			[initOptions]
		);

		const [selectedOption, setSelectedOption] = useState(null);

		useEffect(() => {
			setSelectedOption(
				options.find((opt) => opt.value === value) ?? null
			);
		}, [value]);

		useEffect(() => {
			if (selectedOption) {
				onChange(selectedOption.value);
			}
		}, [selectedOption]);

		return (
			<div className={rootClass} {...rest}>
				<label
					htmlFor={id}
					className="supt-inputSelect__label supt-inputField__label"
					id={labelId}
					data-optional={
						required ? '' : __t('form-input-optional', 'optional')
					}
				>
					{label}
				</label>
				<Select
					inputRef={ref}
					styles={{
						indicatorSeparator: () => ({
							display: 'none',
						}),
					}}
					components={{
						DropdownIndicator: ({ children, ...rest }) => (
							<svg viewBox="0 0 13 8" width="13" height="8">
								<path
									fill="none"
									fillRule="evenodd"
									stroke="#0D2743"
									strokeWidth="1.6"
									d="m11.5 1.5-5 5-5-5"
								/>
							</svg>
						),
					}}
					aria-labelledby={labelId}
					inputId={id}
					classNamePrefix="supt-inputSelect"
					className="supt-inputSelect__input supt-inputField__input"
					onChange={(selectedOption) =>
						setSelectedOption(selectedOption)
					}
					isDisabled={false}
					isSearchable={true}
					name={name}
					placeholder={placeholder}
					options={options}
					isOptionDisabled={(opt) => opt?.disabled ?? false}
					aria-invalid={!!invalid}
					/* @ts-expect-error */
					onBlur={onBlur}
					value={selectedOption}
				/>
				{invalid && typeof invalid === 'string' ? (
					<span
						role="alert"
						className="supt-inputSelect__error supt-inputField__error"
					>
						{invalid}
					</span>
				) : null}
			</div>
		);
	}
) as ForwardedInputSelect;

InputSelect.slug = block.slug;
InputSelect.title = block.title;
